import { $Formutil } from 'react-bootstrap-formutil';
import type { AllowSizeOption, FileUploaderType } from 'components/Uploader';
import { FormCheckType, IdTypeInfoCategoryEnum, OCRTypeEnum, PicProofEnum } from 'types/common';
import { GenderEnum, IdTypeEnum, LicenseEnum } from 'types/customer';

export type UploadIdFieldType = Partial<Record<PicProofEnum, string>>;

export enum LabelFieldEnum {
    ID_CARD = 'id_card',
    ID_CARD2 = 'id_card2',
    PIC_EXEMPTION = 'pic_exemption', // 豁免证明
    PIC_PASSPORT = 'pic_passport', // 护照
    NATIONAL_ID_CARD = 'national_id_card', // 通用身份证
    COMMON_ID_CARD = 'common_id_card',
    RESIDENCE_PERMIT_IDS = 'residence_permit_ids', // 居留许可证
    EMPLOYMENT_PASS_IDS = 'employment_pass_ids' // 新加坡EP证件
}
export interface LabelFieldsItemType {
    label?: string;
    desc?: {
        // 是否需要序号
        needOrderNum: boolean;
        texts: string[];
    };
    fields: PicProofEnum[];
    types?: FileUploaderType[];
    tooltips?: string[];
}

export interface UploadIDProps {
    $formutil: $Formutil<UploadIdFieldType>;
    className?: string;
    // 需要上传的证件类型
    uploadFiles: LabelFieldEnum[];
    // 跟ocr相关的配置项, 仅在需要使用OCR识别证件, 返回证件信息时, 才需要这个配置项
    // 该配置项存在则代表需要使用OCR
    ocrOptions?: {
        // 通过ocr获取信息的处理函数, 存在则代表需要通过ocr来获取用户证件信息
        onOCRDataChange: UploadOcrFunc;
        // 配合onOCRDataChange使用，需要做OCR的证件类型
        ocrType: OCRTypeEnum;
        // 只有身份证件ocr的时候需要license
        license: LicenseEnum | undefined;
        sizeOption?: AllowSizeOption;
    };
    // 仅在上传身份证件时需要
    idCardInfoOptions?: {
        idType: IdTypeEnum | undefined;
        issueCountry: string | undefined;
        category?: IdTypeInfoCategoryEnum;
    };
    // defaultLabelFields 中存在着针对各个证件类型的默认配置, 如果需要展示特殊配置, 可以通过extraLabelFields来控制
    extraLabelFields?: Partial<Record<LabelFieldEnum, Partial<LabelFieldsItemType>>>;
}
// 上传图片接口返回的数据结构类型
export interface UploadFileType {
    url: string;
    filename: string;
}

export interface OCRBaseinfoData {
    real_name: string;
    id_no: string;
    gender: GenderEnum;
    // 部分地区Ocr识别不到详细的地址类型, 这种情况下地址就以home_address返回
    home_address?: string;
    // 省市区和详细地址
    home_city: string;
    home_district: string;
    home_state: string;
    home_detail: string;
    home_postal?: string;
    id_expire_date?: string;
    first_name: string; // 姓的拼音
    last_name: string; // 名的拼音
    // 下面是regula新增的
    id_type?: IdTypeEnum;
    id_version?: string;
    birthday?: string;
    middle_initial?: string; // 中间名
    issue_authority?: string; // 发证机关
}

// 使用OCR接口上传图片时, 返回的数据结构的类型
// 不同证件/不同OCR厂商返回的数据可能不同, 需要以实际返回类型为主
export interface PostOcrTypes extends OCRBaseinfoData, UploadFileType {
    temporary_id_card?: boolean; // 是否是临时身份证
    issue_country_code?: string; // 识别出来的发证国
    country?: string;
    country_code?: string;
    // ocr识别出来的用户信息的合格状态, 1表示合格, 0表示不合格
    ocr_validator: Record<keyof OCRBaseinfoData, FormCheckType>;
}

// 在识别银行卡时返回的数据类型
export interface PostBankCardOcrTypes extends UploadFileType {
    number: string; // 卡号信息
    name: string; // 姓名
    organization: string; // 金融组织 eg.UnionPay、MasterCard、JCB、VISA
    bank: string; // 所属银行
}

export interface UploadOcrFunc {
    (data: PostOcrTypes | PostBankCardOcrTypes, filedName: PicProofEnum): void;
}
